import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Button,
  Form,
  Table,
  FormFeedback,
} from "reactstrap";

import { v4 as uuidv4 } from "uuid";

import { useFormik } from "formik";

import { getPart, updatePart } from "../../../../slices/thunks";
import { categories } from "../../../../common/data/olxCategories";
import { partValidationSchema } from "../../../../models/part";
import { allAttributes } from "../../../../common/data/requiredAttributes";
import {
  yearsSpanAscending,
  yearsSpanDescending,
} from "../../../../common/data/allYearLists";
import Dropzone from "react-dropzone";
import Dragula from "react-dragula";
import { addNewPartImages } from "../../../../helpers/fakebackend_helper";

const DetailsModal = ({ partDetails, show, onCloseClick }) => {
  const dispatch = useDispatch();

  const [files, setFiles] = useState([]);
  const containerRef = useRef(null);

  const user = JSON.parse(sessionStorage.getItem("authUser"));
  const isAdmin = user.role == "Admin" ? true : false;

  const [attributes, setAttributes] = useState([]);
  const handleSelectAttribute = (event, attribute) => {
    let newAttribute = {
      id: uuidv4(),
      parentOlxId: attribute.parentOlxId,
      olxId: attribute.olxId,
      value:
        attribute.options != null
          ? attribute.options[event.target.value].value
          : event.target.value,
      label:
        attribute.options != null
          ? attribute.options[event.target.value].label
          : event.target.value,
    };
    if (!attributes.length) {
      attributes.push(newAttribute);
      setAttributes(attributes);
    } else {
      let updatedAttrs = attributes.map((item) =>
        item.olxId == newAttribute.olxId &&
        item.parentOlxId == newAttribute.parentOlxId
          ? { ...item, ...newAttribute }
          : item
      );
      let existingAttr = attributes.find(
        (item) =>
          item.olxId == newAttribute.olxId &&
          item.parentOlxId == newAttribute.parentOlxId
      );
      if (!existingAttr) updatedAttrs.push(newAttribute);
      setAttributes(updatedAttrs);
    }
  };

  useEffect(() => {
    let attributeValues = [];
    if (partDetails && partDetails.attributes) {
      let requiredAttributes = allAttributes.filter(
        (item) => item.parentOlxId == partDetails.categoryId
      );
      for (let i = 0; i < partDetails.attributes.length; i++) {
        let attr = requiredAttributes.find(
          (item) => item.olxId == partDetails.attributes[i].olxId
        );
        let attrValue =
          attr.options == null
            ? partDetails.attributes[i].option
            : attr.options.find(
                (item) => item.label == partDetails.attributes[i].option
              );
        attributeValues.push({
          id: partDetails.attributes[i].id,
          parentOlxId: attr.parentOlxId,
          olxId: attr.olxId,
          value:
            attr.options == null
              ? partDetails.attributes[i].option
              : attrValue.value,
          label: attrValue.label,
        });
      }
      setAttributes(attributeValues);
    }
  }, []);

  useEffect(() => {
    if (show && partDetails) {
      const existing = (partDetails.images ?? []).map((img, idx) => {
        const url = typeof img === "string" ? img : img.url;
        const serverId = typeof img === "string" ? null : img.id; // <-- keep DB Id

        return {
          id: uuidv4(),
          name: `image-${idx + 1}.jpg`,
          size: 0,
          formattedSize: "existing",
          preview: url,
          index: idx + 1,
          existing: true,
          serverId,
        };
      });
      setFiles(existing);
    }
    if (!show) setFiles([]);
  }, [show, partDetails]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const drake = Dragula([container]);
    drake.on("dragend", () => {
      const rows = container.children || [];
      // Reorder state by current DOM order
      const newOrder = [];
      for (let i = 0; i < rows.length; i++) {
        const id = rows[i].id;
        const f = files.find((x) => x.id === id);
        if (f) {
          f.index = i + 1;
          newOrder.push(f);
        }
      }
      setFiles(newOrder);
    });
    return () => {
      try {
        drake.destroy();
      } catch {}
    };
  }, [show, files.length]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      ...partDetails,
      category: partDetails.categoryId,
    },
    validationSchema: partValidationSchema,
    onSubmit: (values) => {
      let part = {
        id: partDetails.id,
        publicIdentifier: partDetails.publicIdentifier,
        name: values.name,
        catalogNumber: values.catalogNumber,
        location: values.location,
        position: values.position,
        originVehicle: values.originVehicle,
        color: values.color,
        yearStart: values.yearStart,
        yearEnd: values.yearEnd,
        categoryId: values.category,
        attributes: attributes
          .filter((item) => item.parentOlxId == values.category)
          .map((attr) => ({
            id: attr.id,
            olxId: attr.olxId,
            parentOlxId: attr.parentOlxId,
            option: attr.label,
          })),
        status: partDetails.status != 2 ? partDetails.status : 2,
        sellingPrice:
          partDetails.sellingPrice == null
            ? values.sellingPrice
            : partDetails.sellingPrice,
        description: values.description,
        listingId: partDetails.listingId,
        note: partDetails.note,
      };
      dispatch(updatePart(part));
      handleOnCloseClick();
    },
  });

  const handleFormSubmit = (e) => {
    e.preventDefault();
    validation.handleSubmit();
    return false;
  };

  const handleOnCloseClick = () => {
    validation.resetForm();
    setAttributes([]);
    onCloseClick();
  };

  function formatBytes(bytes) {
    if (bytes === 0) return "0 Bytes";
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + " " + sizes[i];
  }

  function handleAcceptedFiles(uploadedFiles) {
    let i = files.length;
    const enriched = uploadedFiles.map((f) => {
      i++;
      return Object.assign(f, {
        preview: URL.createObjectURL(f),
        formattedSize: formatBytes(f.size),
        id: uuidv4(),
        index: i,
        existing: false,
      });
    });
    setFiles((prev) => [...prev, ...enriched]);
  }

  function deleteFile(file) {
    const proceed = window.confirm("Obrisati ovu sliku?");
    if (!proceed) return;

    const doLocalRemove = () => {
      const modified = files.filter((item) => item.id !== file.id);
      modified.forEach((item, i) => (item.index = i + 1));
      setFiles(modified);
    };

    // If it's already on server (existing + has serverId) -> call API
    if (file.existing && file.serverId) {
      deletePartImage(partDetails.id, file.serverId)
        .then(() => {
          doLocalRemove();
          dispatch(getPart(partDetails.id));
          // optional: toast.info("Slika obrisana.");
        })
        .catch(() => {
          // optional: toast.error("Brisanje slike nije uspjelo.");
        });
    } else {
      // Not uploaded yet (new row) -> just remove locally
      doLocalRemove();
    }
  }

  const [uploading, setUploading] = useState(false);

  const handleUploadNew = async () => {
    const newFiles = files.filter((f) => !f.existing); // only freshly added
    if (newFiles.length === 0) return;

    try {
      setUploading(true);
      await addNewPartImages(partDetails.id, newFiles);
      // Mark them as existing in the UI
      setFiles((prev) =>
        prev.map((f) =>
          f.existing ? f : { ...f, existing: true, formattedSize: "existing" }
        )
      );
      dispatch(getPart(partDetails.id));
      // optional: toast.success("Nove slike su uploadovane.");
    } catch (e) {
      // optional: toast.error("Upload slika nije uspio.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal
      id="detailsModal"
      isOpen={show}
      toggle={handleOnCloseClick}
      centered
      backdrop={"static"}
    >
      <ModalHeader className="modal-title" toggle={handleOnCloseClick}>
        {" "}
        {"Izmjene za: " + partDetails.name}{" "}
      </ModalHeader>
      <Form className="tablelist-form" onSubmit={handleFormSubmit}>
        <ModalBody>
          {/* Naziv dijela, Kataloski broj */}
          <div className="row gy-4 mb-3">
            <div className="col-md-8">
              <Input
                name="name"
                type="text"
                className="form-control"
                placeholder="Naziv dijela"
                validate={{ required: { value: true } }}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.name || ""}
                invalid={
                  validation.touched.name && validation.errors.name
                    ? true
                    : false
                }
              />{" "}
              {validation.touched.name && validation.errors.name ? (
                <FormFeedback type="invalid">
                  {validation.errors.name}
                </FormFeedback>
              ) : null}
            </div>
            <div className="col-md-4">
              <Input
                name="catalogNumber"
                type="text"
                className="form-control"
                placeholder="Kataloški broj"
                validate={{ required: { value: false } }}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.catalogNumber || ""}
                invalid={
                  validation.touched.catalogNumber &&
                  validation.errors.catalogNumber
                    ? true
                    : false
                }
              />{" "}
              {validation.touched.catalogNumber &&
              validation.errors.catalogNumber ? (
                <FormFeedback type="invalid">
                  {validation.errors.catalogNumber}
                </FormFeedback>
              ) : null}
            </div>
          </div>
          {/*Lokacija dijela, Broj sasije vozila na kojem je dio, */}
          <div className="row gy-4 mb-3">
            <div className="col-md-8">
              <Input
                name="originVehicle"
                type="text"
                className="form-control"
                placeholder="Broj šasije vozila"
                validate={{ required: { value: false } }}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.originVehicle || ""}
                invalid={
                  validation.touched.originVehicle &&
                  validation.errors.originVehicle
                    ? true
                    : false
                }
              />
              {validation.touched.originVehicle &&
              validation.errors.originVehicle ? (
                <FormFeedback type="invalid">
                  {validation.errors.originVehicle}
                </FormFeedback>
              ) : null}
            </div>
            <div className="col-md-4">
              <Input
                name="location"
                type="text"
                className="form-control"
                placeholder="Lokacija"
                validate={{ required: { value: true } }}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.location || ""}
                invalid={
                  validation.touched.location && validation.errors.location
                    ? true
                    : false
                }
              />
              {validation.touched.location && validation.errors.location ? (
                <FormFeedback type="invalid">
                  {validation.errors.location}
                </FormFeedback>
              ) : null}
            </div>
          </div>
          {/*Boja, Strana*/}
          <div className="row gy-4 mb-3">
            <div className="col-6">
              <Input
                name="color"
                type="text"
                className="form-control"
                placeholder="Boja"
                validate={{ required: { value: false } }}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.color || ""}
                invalid={
                  validation.touched.color && validation.errors.color
                    ? true
                    : false
                }
              />
              {validation.touched.color && validation.errors.color ? (
                <FormFeedback type="invalid">
                  {validation.errors.color}
                </FormFeedback>
              ) : null}
            </div>
            <div className="col-6">
              <Input
                name="position"
                type="text"
                className="form-control"
                placeholder="Strana"
                validate={{ required: { value: false } }}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.position || ""}
                invalid={
                  validation.touched.position && validation.errors.position
                    ? true
                    : false
                }
              />
              {validation.touched.position && validation.errors.position ? (
                <FormFeedback type="invalid">
                  {validation.errors.position}
                </FormFeedback>
              ) : null}
            </div>
          </div>
          {/*Godiste rasponi*/}
          <div className="row gy-4 mb-3">
            <div className="col-md-6">
              <Input
                name="yearStart"
                type="select"
                className="form-select"
                validate={{ required: { value: false } }}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.yearStart || ""}
                invalid={
                  validation.touched.yearStart && validation.errors.yearStart
                    ? true
                    : false
                }
              >
                {yearsSpanAscending.map((item) => (
                  <option value={item.value} key={item.value}>
                    {item.label}
                  </option>
                ))}
              </Input>
              {validation.touched.yearStart && validation.errors.yearStart ? (
                <FormFeedback type="invalid">
                  {validation.errors.yearStart}
                </FormFeedback>
              ) : null}
            </div>
            <div className="col-md-6">
              <Input
                name="yearEnd"
                type="select"
                className="form-select"
                validate={{ required: { value: false } }}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.yearEnd || ""}
                invalid={
                  validation.touched.yearEnd && validation.errors.yearEnd
                    ? true
                    : false
                }
              >
                {yearsSpanDescending.map((item) => (
                  <option value={item.value} key={item.value}>
                    {item.label}
                  </option>
                ))}
              </Input>
              {validation.touched.yearEnd && validation.errors.yearEnd ? (
                <FormFeedback type="invalid">
                  {validation.errors.yearEnd}
                </FormFeedback>
              ) : null}
            </div>
          </div>
          {/* Olx kategorija dijela */}
          <div className="row gy-4 mb-3">
            <div className="col-md-8 mx-auto">
              <Input
                name="category"
                type="select"
                className="form-select"
                validate={{ required: { value: true } }}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.category || ""}
                invalid={
                  validation.touched.category && validation.errors.category
                    ? true
                    : false
                }
              >
                {categories.map((item) => (
                  <option value={item.value} key={item.value}>
                    {item.label}
                  </option>
                ))}
              </Input>
              {validation.touched.category && validation.errors.category ? (
                <FormFeedback type="invalid">
                  {validation.errors.category}
                </FormFeedback>
              ) : null}
            </div>
          </div>
          <div className="row gy-4 mb-3">
            {allAttributes
              .filter((item) => item.parentOlxId == validation.values.category)
              .map((item) => (
                <div className="col-md-6 mx-auto" key={item.olxId}>
                  {item.inputType == "number" ? (
                    <Input
                      name={item.name}
                      type="number"
                      className="form-control"
                      placeholder={
                        item.olxId === 5078 ? "Jačina (Ah)" : "Jačina (W)"
                      }
                      value={
                        attributes.find((attr) => attr.olxId == item.olxId)
                          ?.value ?? ""
                      }
                      onChange={(e) => {
                        handleSelectAttribute(e, item);
                      }}
                    />
                  ) : (
                    <Input
                      name={item.name}
                      type="select"
                      className="form-select"
                      value={
                        attributes.find((attr) => attr.olxId == item.olxId)
                          ?.value ?? 0
                      }
                      onChange={(e) => {
                        handleSelectAttribute(e, item);
                      }}
                    >
                      {item.options.map((opt) => (
                        <option value={opt.value} key={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Input>
                  )}
                </div>
              ))}
          </div>
          {/* Status dijela, Cijena dijela */}
          {!isAdmin ? null : (
            <div className="row gy-4 mb-3">
              <div className="col-md-6 input-group">
                <Input
                  name="sellingPrice"
                  type="number"
                  className="form-control"
                  placeholder="Cijena"
                  validate={{ required: { value: isAdmin } }}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.sellingPrice}
                  invalid={
                    validation.touched.sellingPrice &&
                    validation.errors.sellingPrice
                      ? true
                      : false
                  }
                />
                <div className="input-group-text">BAM</div>
                {validation.touched.sellingPrice &&
                validation.errors.sellingPrice ? (
                  <FormFeedback type="invalid">
                    {validation.errors.sellingPrice}
                  </FormFeedback>
                ) : null}
              </div>
            </div>
          )}
          <div className="mb-3">
            <Input
              name="description"
              type="textarea"
              className="form-control"
              placeholder="Opis"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.description}
            />
          </div>

          <div className="mb-3">
            <Dropzone
              accept={{ "image/*": [] }}
              onDrop={(acceptedFiles) => handleAcceptedFiles(acceptedFiles)}
            >
              {({ getRootProps, getInputProps }) => (
                <div className="dropzone dz-clickable" {...getRootProps()}>
                  <input {...getInputProps()} />
                  <div className="dz-message needsclick">
                    <i className="display-5 text-muted ri-upload-cloud-2-fill" />
                    <p>Drop files here or click to upload.</p>
                  </div>
                </div>
              )}
            </Dropzone>

            {files.length > 10 && (
              <div className="mt-2">
                <div className="alert alert-danger text-center fs-20">
                  Dodali ste {files.length} slika, što je više od 10 slika.
                </div>
              </div>
            )}

            <div className="list-unstyled mb-0" id="file-previews">
              <div className="table-responsive mb-1">
                <Table className="align-middle table table-borderless">
                  <tbody id="image-container" ref={containerRef}>
                    {files.map((f) => (
                      <tr
                        id={f.id}
                        key={f.id}
                        className="dz-processing dz-image-preview dz-success dz-complete"
                      >
                        <td>
                          <img
                            src={f.preview}
                            alt={f.name}
                            className="avatar-md rounded me-2"
                            data-dz-thumbnail=""
                          />
                          <span className="text-muted fw-bold">
                            {f.name} ({f.formattedSize})
                            {f.existing ? " • existing" : ""}
                          </span>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-sm btn-soft-danger remove-list"
                            onClick={() => deleteFile(f)}
                          >
                            <i className="ri-delete-bin-5-fill align-bottom" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="hstack gap-2 justify-content-end">
            <Button
              type="button"
              color="dark"
              outline
              onClick={() => {
                handleOnCloseClick();
              }}
            >
              {" "}
              Zatvori{" "}
            </Button>
            <Button
              type="button"
              color="primary"
              outline
              onClick={handleUploadNew}
              disabled={!files.some((f) => !f.existing)}
            >
              Upload nove slike
            </Button>
            <Button type="submit" color="success" outline>
              {" "}
              Ažuriraj{" "}
            </Button>
          </div>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default DetailsModal;
